import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { TemplatePriorityBadge } from './TemplatePriorityBadge';
import { TemplateTaskPopoverForm } from './TemplateTaskPopoverForm';
import type { TemplateGroup } from '../types';
import type { TemplateTaskFormValues } from '../schemas';

interface TemplateTasksTabProps {
  groups: TemplateGroup[];
  editable: boolean;
  onAddGroup?: (name: string) => void;
  onRemoveGroup?: (groupIndex: number) => void;
  onAddTask?: (groupIndex: number, task: TemplateTaskFormValues) => void;
  onRemoveTask?: (groupIndex: number, taskIndex: number) => void;
  isPending?: boolean;
}

export function TemplateTasksTab({
  groups,
  editable,
  onAddGroup,
  onRemoveGroup,
  onAddTask,
  onRemoveTask,
  isPending = false,
}: TemplateTasksTabProps) {
  const { t } = useTranslation('projectTemplates');
  const [newGroupName, setNewGroupName] = useState('');

  function handleAddGroup() {
    if (!newGroupName.trim() || !onAddGroup) return;
    onAddGroup(newGroupName.trim());
    setNewGroupName('');
  }

  return (
    <div className="flex flex-col gap-4" data-testid="project-templates-tasks-tab">
      {groups.length === 0 ? (
        <p
          className="py-4 text-center text-sm text-muted-foreground"
          data-testid="project-templates-tasks-empty"
        >
          {t('preview.emptyStates.tasks')}
        </p>
      ) : (
        groups.map((group, groupIndex) => (
          <div key={`${group.name}-${groupIndex}`} className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {group.name}
                <span className="ml-2 normal-case">({group.tasks.length})</span>
              </p>
              {editable && onRemoveGroup && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  disabled={isPending}
                  onClick={() => onRemoveGroup(groupIndex)}
                  data-testid={`project-templates-group-remove-${groupIndex}`}
                >
                  {t('actions.remove')}
                </Button>
              )}
            </div>

            <div className="flex flex-col gap-2">
              {group.tasks.map((task, taskIndex) => (
                <div
                  key={`${task.title}-${taskIndex}`}
                  className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                  data-testid={`project-templates-task-row-${groupIndex}-${taskIndex}`}
                >
                  <span className="text-sm">{task.title}</span>
                  <div className="flex items-center gap-2">
                    <TemplatePriorityBadge priority={task.priority} />
                    {editable && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-6"
                            disabled={isPending}
                            aria-label={t('actions.remove')}
                            onClick={() => onRemoveTask?.(groupIndex, taskIndex)}
                            data-testid={`project-templates-task-remove-${groupIndex}-${taskIndex}`}
                          >
                            <X className="size-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('actions.remove')}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
              ))}

              {editable && (
                <TemplateTaskPopoverForm
                  onAdd={(task) => onAddTask?.(groupIndex, task)}
                  isPending={isPending}
                />
              )}
            </div>
          </div>
        ))
      )}

      {editable && onAddGroup && (
        <div className="flex items-center gap-2">
          <Input
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder={t('editor.tasks.groupNameLabel')}
            disabled={isPending}
            data-testid="project-templates-group-name-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isPending || !newGroupName.trim()}
            onClick={handleAddGroup}
            data-testid="project-templates-group-add-button"
          >
            <Plus className="mr-1 size-4" />
            {t('editor.tasks.addGroupLabel')}
          </Button>
        </div>
      )}
    </div>
  );
}
